import { Component, OnInit } from '@angular/core';
import { OrderService } from '../services/order/order.service';
import { ActivatedRoute } from '@angular/router';
import { LocationsService } from '../services/locations/locations.service';
import { PricingService } from '../services/pricing/pricing.service';
import { MenuStore } from '../store/menu/menu.store';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentService } from '../services/payment/payment.service';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit {
  // Basic
  public order;
  public pricing;

  //Error Message
  public errorMessage = '';
  public showErrorMessage: boolean;

  // View State
  public payWithCardIsActive: boolean = true;
  public payWithGiftCardIsActive: boolean = false;
  public CCViewIsActive: boolean = true;
  public GCViewIsActive: boolean = false;
  public showSummaryFold: boolean = false;

  // Ticket
  public ticket;
  public ticketID;
  public totals;

  // Location
  public locationID;
  public locationName;

  // Credit Card
  private currentYear;
  private cardYearsArr = [];
  private cardNumber;
  private cardExpireMonth = 'MM';
  private cardExpireYear = 'YYYY';
  private cardCVC;
  private cardTip: number = 0;

  // Gift Card
  public giftCardNumber = '';

  // Paying State
  public orderPaid: boolean = false;
  public payingBill: boolean = false;
  public alreadyPaid: boolean = false;

  // Forms
  public CCForm: FormGroup;

  constructor(
    private _orderService: OrderService,
    private _menuStore: MenuStore,
    private _route: ActivatedRoute,
    private _pricingService: PricingService,
    private _locationsService: LocationsService,
    private _paymentService: PaymentService,
    private _fb: FormBuilder
  ) {
    // Build the Credit Card Form
    this.CCForm = this._fb.group({
      nameOnCard: [
        '',
        Validators.compose([Validators.maxLength(35), Validators.required]),
      ],
      cardNumber: [
        '',
        Validators.compose([
          Validators.maxLength(16),
          // 15 for AMEX
          Validators.minLength(15),
          Validators.required,
        ]),
      ],
      cardExpireMonth: ['MM', Validators.required],
      cardExpireYear: ['YYYY', Validators.required],
      cardCVC: [
        '',
        Validators.compose([Validators.maxLength(3), Validators.required]),
      ],
    });
    console.log('CREDIT CARD FORM:', this.CCForm);
  }

  ngOnInit() {
    // Get the order observable
    this._orderService.orderSource.subscribe(order => (this.order = order));

    // Get the pricing observable
    this._orderService.orderPricingSource.subscribe(
      pricing => (this.pricing = pricing)
    );

    // Get the ticket observable
    this._orderService.ticket.subscribe(ticket => (this.ticket = ticket));

    // Get the location ID Observable
    this._orderService.locationID.subscribe(
      locationID => (this.locationID = locationID)
    );

    // Get the ticket ID observable
    this._orderService.ticketID.subscribe(id => (this.ticketID = id));

    // Get the location name Observable
    this._locationsService.locationName.subscribe(
      name => (this.locationName = name)
    );

    // Grabs the values if the page was reloaded
    this.pageReloadCheck();

    // gets the current year for the years for the credit card
    let date = new Date();
    this.currentYear = date.getFullYear();
    this.getTwentyYears(this.currentYear);
  }

  public openSummaryFold(): void {
    this.showSummaryFold = !this.showSummaryFold;
  }
  /**
   * This function runs when the user submits the payment form
   */
  public payBill() {
    console.log('TICKET ID', this.ticketID);
    console.log(this.CCForm);

    // Check if form is valid before sending
    if (!this.CCForm.invalid) {
      // Loader animation starts
      this.payingBill = true;

      let paymentInfo = {
        type: 'card_not_present',
        amount: this.ticket.totals.total,
        tip: this.cardTip,
        card_info: {
          exp_month: parseFloat(this.CCForm.controls.cardExpireMonth.value),
          exp_year: parseFloat(this.CCForm.controls.cardExpireYear.value),
          cvc2: this.CCForm.controls.cardCVC.value.toString(),
          number: this.CCForm.controls.cardNumber.value.toString(),
        },
      };
      console.log('PAYMENT INFO:', paymentInfo);

      console.log(this.ticket, this.order);

      if (this.ticketHasItems(this.ticket, this.order)) {
        this.creditCardPayment(paymentInfo);
      } else {
        console.log('adding items to ticket');
        this._addItemsToTicket();
      }
    } else {
      this._showErrorMessage('Please fill out the required fields');
      this.showErrorMessage = true;
    }
  }

  // Checks to see if the ticket has the same amount of items
  // as the order from the user
  public ticketHasItems(ticket, order) {
    console.log('TICKET', ticket);
    console.log('ORDER', order);
    if (order.length === ticket._embedded.items.length) {
      return true;
    } else if (order.length < ticket._embedded.items.length) {
      return true;
    } else if (order.length > ticket._embedded.items.length) {
      return false;
    }
  }

  public creditCardPayment(paymentInfo) {
    this._paymentService
      .payForCheckWithCreditCard(this.ticketID, this.locationID, paymentInfo)
      .subscribe(
        result => {
          console.log('paid ticket', result);
          if (result._embedded.ticket.totals.due === 0) {
            this.orderPaid = true;
            window.scrollTo(0, 0);
            this.payingBill = false;
            this._orderService.clearOrderAndTicket();
          }
        },
        error => {
          console.log('error:', error.error);
          this.payingBill = false;
          this._showErrorMessage(
            'Sorry! We are having issues. Please try again later.'
          );
          // Get the ticket coming back after error.
          this._orderService
            .getTicketByID(this.ticketID, this.locationID)
            .subscribe(
              result => {
                console.log('loaded result', result);
                this.ticket = result;
                this.generateTotals();
              },
              error => {
                console.log('cant get ticket by id');
              }
            );
        }
      );
  }

  // Pays with giftcard
  public giftCardPayment() {
    let paymentInfo = {
      type: 'gift_card',
      amount: this.ticket.totals.total,
      tip: 0,
      card_info: {
        number: this.giftCardNumber.toString(),
      },
    };
    console.log('PAYMENT INFO GC:', paymentInfo);

    this._paymentService
      .payForCheckWithGiftCard(this.ticketID, this.locationID, paymentInfo)
      .subscribe(
        result => {
          console.log('GIFT CARD:', result);
          if (result._embedded.ticket.totals.due === 0) {
            console.log('PAID');
            this.orderPaid = true;
            this.payingBill = false;
            // this._orderService.clearOrderAndTicket();
          }
        },
        error => {
          console.log('could not pay for check with gift card', error);
          this.payingBill = false;
        }
      );
  }

  // Click from the front end that begins the payment for the giftcard
  public payWithGiftCard() {
    if (this.ticketHasItems(this.ticket, this.order)) {
      this.giftCardPayment();
    } else {
      this._orderService
        .addItemsToTicket(this.locationID, this.ticketID, this.order)
        .subscribe(
          result => {
            this._orderService.updateTicket(result);
            console.log('UPDATED TICKET AFTER ADDING ITEMS', result);
            this.ticket = result;
            this.payingBill = true;
            console.log(this.ticket);
            this.giftCardPayment();
          },
          error => {
            console.log(error, 'cant add items to ticket');
          }
        );
    }
  }

  // View State
  public activatePayWithCard() {
    this.CCViewIsActive = true;
    this.GCViewIsActive = false;
  }

  // View State
  public activatePayWithGiftCard() {
    this.CCViewIsActive = false;
    this.GCViewIsActive = true;
  }

  // Gets the years for the credit card best on the current year
  private getTwentyYears(currentYear) {
    let maxYear = currentYear + 15;
    for (let i = currentYear; maxYear >= i; i++) {
      this.cardYearsArr.push(i);
    }
  }

  // Adds Items to ticket
  private _addItemsToTicket() {
    this._orderService
      .addItemsToTicket(this.locationID, this.ticketID, this.order)
      .subscribe(
        result => {
          this._orderService.updateTicket(result);
          console.log('UPDATED TICKET AFTER ADDING ITEMS', result);
          this.ticket = result;
          this.generateTotals();
          if (this.ticket.totals.due === 0) {
            this.alreadyPaid = true;
          }
        },
        error => {
          console.log('could not add items to ticket', error);
        }
      );
  }

  private generateTotals() {
    this.totals = this._pricingService.generateTotals(this.ticket.totals);
    console.log('TOTALS', this.totals);
  }

  private _showErrorMessage(message) {
    this.errorMessage = message;
    this.showErrorMessage = true;
  }

  private pageReloadCheck() {
    // Grabs the location ID if the page was reloaded.
    if (!this.locationID) {
      this.locationID = localStorage.getItem('locationID');
      console.log('LOCATIONID:', this.locationID);
    }

    // Grabs the location name if the page was reloaded
    if (!this.locationName && localStorage.getItem('locationName')) {
      this.locationName = localStorage.getItem('locationName');
    } else if (this.locationID) {
      this.locationName = this._locationsService
        .getLocationById(this.locationID)
        .subscribe(location => {
          this.locationName = location.name;
          console.log('LOCATION NAME:', this.locationName);
        });
    }

    this.locationName = localStorage.getItem('locationName');

    // Grabs the order if the page was reloaded
    if (this.order == null && localStorage.getItem('order')) {
      this.order = JSON.parse(localStorage.getItem('order'));
      console.log('ORDER', this.order);
    }

    // Grabs the ticket id if the page was reloaded
    if (!this.ticketID) {
      this._route.params.subscribe(params => {
        if (typeof params.ticketID === 'string') {
          this.ticketID = params.ticketID;
          console.log('TICKET ID', this.ticketID);
        }
      });
    }

    // Grabs the pricing object if the page was reloaded and the current componet value is falsy
    if (!this.pricing && localStorage.getItem('pricingObject')) {
      this.pricing = JSON.parse(localStorage.getItem('pricingObject'));
      console.log('pricing from localstorage', this.pricing);
    }

    // Gets the ticket if reloaded and checks to see if the order has been paid
    if (!this.ticket) {
      let id: string = '';
      this._route.params.subscribe(result => {
        id = result.ticketID;
        this._orderService.getTicketByID(id, this.locationID).subscribe(
          result => {
            console.log('loaded result', result);
            this.ticket = result;

            if (!this.ticketHasItems(this.ticket, this.order)) {
              this._addItemsToTicket();
              console.log('ADDED ITEMS TO TICKET HERE');
              console.log('first time loaded ran generate totals');
            } else {
              this.generateTotals();
            }
          },
          error => {
            console.log('cant get ticket by id');
          }
        );
      });
    } else {
      if (!this.ticketHasItems(this.ticket, this.order)) {
        console.log('ADDED ITEMS TO TICKET HERE');
        this._addItemsToTicket();
      } else {
        this.generateTotals();
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/checkout/checkout.component.ts